/**
 * The one sanctioned mutable structure in core (ADR-0004 · plan §3.1 L483).
 *
 * Series generation is pure given (security, window), but recomputing every
 * gaussian walk on each chart paint is wasteful. Callers create a cache via
 * this factory and inject it; nothing in analytics/selectors may import it.
 *
 * The Map is closed over — callers never see the store, though `set` is part of
 * the injected handle. Contract: cold result deep-equals warm result; a fresh
 * cache yields identical output. Cached bar arrays are frozen.
 *
 * Bound: unbounded by construction, but on the sanctioned call path the key
 * space is UNIVERSE_SIZE × WINDOW_KEYS.length = 516 entries (~47.6k floats,
 * ~0.4 MB) and fills in single-digit milliseconds. Callers that synthesise
 * securities — varying price/vol/ret, which the key includes — widen that space
 * without limit and should use a short-lived cache instance.
 */
/** Opaque injected handle; Map stays private inside the factory. */
export interface SeriesCache {
  readonly get: (key: string) => readonly number[] | undefined;
  readonly set: (key: string, bars: readonly number[]) => void;
}

export function createSeriesCache(): SeriesCache {
  const store = new Map<string, readonly number[]>();
  return {
    get: (key) => store.get(key),
    set: (key, bars) => {
      store.set(key, bars);
    },
  };
}

interface SeriesCacheKeyParts {
  readonly ticker: string;
  readonly window: string;
  readonly price: number;
  readonly vol: number;
  readonly periodRet: number;
}

/* Package-internal, not module-private: generate-series.ts needs it, and the
   package `exports` map resolves deep paths. Containment is the dep-cruiser rule
   `only-series-adapters-and-barrel-import-series-cache`, not this export. */
/** Key scheme — includes every input that changes the walk. */
export function seriesCacheKey(parts: SeriesCacheKeyParts): string {
  return [parts.ticker, parts.window, parts.price, parts.vol, parts.periodRet].join('|');
}
